import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Sparkles, Gift, ArrowRight, Trophy, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import Confetti from 'react-confetti';

const API = process.env.REACT_APP_BACKEND_URL;

const SpinWheelPage = () => {
  const navigate = useNavigate();
  const { user, token, isAuthenticated } = useAuth();
  const [spinning, setSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [result, setResult] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [hasSpun, setHasSpun] = useState(false);

  const prizes = [
    { label: '+5 Entries', value: 5, color: '#FF3B30', description: '5 bonus entries added to your referral rewards!' },
    { label: '+10 Entries', value: 10, color: '#FF9500', description: '10 bonus entries - great spin!' },
    { label: '+3 Entries', value: 3, color: '#FFCC00', description: '3 bonus entries added!' },
    { label: 'Try Again', value: 0, color: '#34C759', description: 'Better luck next time! Come back tomorrow.' },
    { label: '+15 Entries', value: 15, color: '#007AFF', description: '15 bonus entries - amazing!' },
    { label: '+7 Entries', value: 7, color: '#5856D6', description: '7 bonus entries added!' },
    { label: 'VIP Access', value: 0, color: '#AF52DE', description: 'Exclusive access to premium giveaways!', special: true },
    { label: '+20 Entries', value: 20, color: '#FF2D55', description: '20 bonus entries - JACKPOT!' },
  ];

  useEffect(() => {
    // Check if user has already spun today
    const lastSpin = localStorage.getItem(`spin_${user?.user_id}`);
    if (lastSpin) {
      const lastSpinDate = new Date(lastSpin);
      const today = new Date();
      if (lastSpinDate.toDateString() === today.toDateString()) {
        setHasSpun(true);
      }
    }
  }, [user]);

  const spinWheel = async () => {
    if (spinning || hasSpun) return;
    setSpinning(true);
    setResult(null);

    // Server determines the prize (authoritative, single spin per day)
    let prizeIndex = 0;
    let serverError = null;
    try {
      const res = await fetch(`${API}/api/referrals/spin`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await res.json();
      if (!res.ok) {
        serverError = data.detail || 'Unable to spin right now.';
      } else {
        prizeIndex = data.prize_index;
      }
    } catch (error) {
      serverError = 'Network error. Please try again.';
    }

    if (serverError) {
      setSpinning(false);
      setHasSpun(true);
      setResult({ label: serverError, value: 0, color: '#8E8E93', description: serverError });
      return;
    }

    const degreesPerPrize = 360 / prizes.length;
    const extraSpins = 5 * 360;
    const targetRotation = extraSpins + (360 - (prizeIndex * degreesPerPrize + degreesPerPrize / 2));

    setRotation(prev => prev + targetRotation);

    setTimeout(() => {
      setSpinning(false);
      setResult(prizes[prizeIndex]);
      setShowConfetti(true);
      setHasSpun(true);

      // Save spin timestamp locally for immediate UX (server is source of truth)
      localStorage.setItem(`spin_${user?.user_id}`, new Date().toISOString());

      setTimeout(() => setShowConfetti(false), 5000);
    }, 5000);
  };

  if (!isAuthenticated) {
    navigate('/login');
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900 flex flex-col items-center justify-center p-4" data-testid="spin-wheel-page">
      {showConfetti && <Confetti recycle={false} numberOfPieces={300} />}
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-8"
      >
        <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit' }}>
          🎰 Spin to Win!
        </h1>
        <p className="text-xl text-white/80">
          Welcome {user?.name}! Spin the wheel for bonus entries.
        </p>
      </motion.div>

      {/* Wheel Container */}
      <div className="relative mb-8">
        {/* Pointer */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-2 z-10">
          <div className="w-0 h-0 border-l-[20px] border-r-[20px] border-t-[35px] border-l-transparent border-r-transparent border-t-yellow-400 drop-shadow-lg" />
        </div>
        
        {/* Wheel */}
        <div 
          className="w-80 h-80 sm:w-96 sm:h-96 rounded-full relative shadow-2xl"
          style={{ 
            transform: `rotate(${rotation}deg)`,
            transition: spinning ? 'transform 5s cubic-bezier(0.17, 0.67, 0.12, 0.99)' : 'none',
            background: `conic-gradient(${prizes.map((p, i) => `${p.color} ${i * (100/prizes.length)}% ${(i + 1) * (100/prizes.length)}%`).join(', ')})`
          }}
        >
          {/* Prize Labels */}
          {prizes.map((prize, index) => {
            const angle = (360 / prizes.length) * index + (360 / prizes.length / 2);
            return (
              <div
                key={prize.id || `prize-${index}`}
                className="absolute w-full h-full flex items-center justify-center"
                style={{ transform: `rotate(${angle}deg)` }}
              >
                <span 
                  className="text-white text-xs sm:text-sm font-bold absolute"
                  style={{ 
                    transform: `translateY(-140px) rotate(0deg)`,
                    textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
                  }}
                >
                  {prize.label}
                </span>
              </div>
            );
          })}
          
          {/* Center */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 bg-white rounded-full shadow-xl flex items-center justify-center z-10">
            <Sparkles className="w-10 h-10 text-[#FF3B30]" />
          </div>
        </div>
        
        {/* Glow effect */}
        <div className="absolute inset-0 rounded-full bg-gradient-to-r from-yellow-400/20 to-orange-500/20 blur-xl -z-10" />
      </div>

      {/* Spin Button or Result */}
      <AnimatePresence mode="wait">
        {!result ? (
          <motion.div
            key="spin-button"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {hasSpun ? (
              <div className="text-center">
                <p className="text-white/80 mb-4">You've already spun today!</p>
                <Button
                  onClick={() => navigate('/dashboard')}
                  className="bg-white text-purple-900 hover:bg-white/90 px-8 py-6 text-lg rounded-xl"
                >
                  Go to Dashboard
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            ) : (
              <Button
                onClick={spinWheel}
                disabled={spinning}
                className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-black px-12 py-6 text-xl font-bold rounded-full shadow-lg shadow-yellow-500/30 transform hover:scale-105 transition-all"
              >
                {spinning ? (
                  <>
                    <Sparkles className="w-6 h-6 mr-2 animate-spin" />
                    Spinning...
                  </>
                ) : (
                  <>
                    <Gift className="w-6 h-6 mr-2" />
                    SPIN NOW!
                  </>
                )}
              </Button>
            )}
          </motion.div>
        ) : (
          <motion.div
            key="result"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center bg-white/10 backdrop-blur-sm rounded-3xl p-8 max-w-md"
          >
            <div className="text-6xl mb-4">
              {result.special ? '🌟' : result.value > 10 ? '🎉' : result.value > 0 ? '✨' : '😊'}
            </div>
            <h2 className="text-3xl font-bold text-white mb-2">{result.label}</h2>
            <p className="text-white/80 mb-6">{result.description}</p>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={() => navigate('/giveaways')}
                className="bg-[#FF3B30] hover:bg-[#DC2626] text-white px-6 py-3 rounded-xl"
              >
                <Trophy className="w-5 h-5 mr-2" />
                Enter Giveaways
              </Button>
              <Button
                onClick={() => navigate('/dashboard/affiliate')}
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10 px-6 py-3 rounded-xl"
              >
                <Star className="w-5 h-5 mr-2" />
                Share & Earn More
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bottom tip */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="text-white/50 text-sm mt-8"
      >
        💡 Tip: Refer friends to earn more spins and entries!
      </motion.p>
    </div>
  );
};

export default SpinWheelPage;
